import * as yup from "yup";
import { StringHelper } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import Drawer from "../../../assets/vfx/drawer/drawer";
import Form from "../../../assets/vfx/form/form";
import { RssFeedDto } from "../../../services/dto";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../assets/utils/lib";

export interface RssFeedLookupProps {
  communities: DropdownOption[];
  rssFeedType: DropdownOption[];
}

interface RssFeedFormProps {
  data?: RssFeedDto;
  loading?: boolean;
  show: boolean;
  onHide: () => void;
  onSave: (values: RssFeedDto) => DrawerFormSaveResult;
  lookups?: RssFeedLookupProps;
  title: string;
  communityId?: string;
  rssFeedTypeId?: string;
  disableCommunity?: boolean;
  disableRSSFeedType?: boolean;
}

const validationSchema = yup.object().shape({
  communityId: yup.string().required("Community is required."),
  rsSfeedTypeId: yup.string().required("RssFeed Type is required."),
  name: yup.string().required("Name is required."),
  description: yup.string().required("Description is required."),
  url: yup.string().url("Invalid URL.").required("Url is required."),
  baseUrlLink: yup.string().nullable().url("Invalid URL.").optional(),
  maxDisplay: yup
    .number()
    .required("Max Display is required.")
    .min(0, "Please enter valid max display."),
  highlightDisplayCount: yup
    .number()
    .min(0, "Please enter valid display count.")
    .required("Highlight Display Count is required."),
});

export const RssFeedForm = (props: RssFeedFormProps) => {
  let rssFeed: RssFeedDto | undefined = props.data;
  const header: string =
    StringHelper.whenNullOrEmpty(rssFeed?.id, Constants.emptyGuid) ===
    Constants.emptyGuid
      ? `New ${props.title}`
      : `Edit ${props.title}`;

  const onSubmit = (data: RssFeedDto) => {
    return Promise.resolve(props.onSave(data)).then((result) => {
        if (hasClientErrorOperationStatusCode(result)) {
            return;
        }

        props.onHide();
    });
  };

  return (
    <Drawer.Form
      className="w-30"
      show={props.show}
      backdrop={true}
      placement="end"
      scroll={true}
      onHide={props.onHide}
      title={header}
      initialValues={
        rssFeed ?? {
          communityId: props.communityId,
          rsSfeedTypeId: props.rssFeedTypeId,
        }
      }
      validationSchema={validationSchema}
      on_Submit={onSubmit}
      enableReinitialize
    >
      <Form.PanelSection iconClassName="bi bi-info-circle" title="1. Basic Information">
        <Form.Dropdown
          name="communityId"
          label="Community"
          isLoading={props.loading}
          disabled={props.disableCommunity}
          blankOption={{
            key: Constants.emptyGuid,
            display: "Select Community",
            value: Constants.emptyString,
          }}
          options={props.lookups?.communities}
        />

        <Form.Dropdown
          name="rsSfeedTypeId"
          label="RSS Feed Type"
          isLoading={props.loading}
          disabled={props.disableRSSFeedType}
          blankOption={{
            key: Constants.emptyGuid,
            display: "Select RSS Feed Type",
            value: Constants.emptyString,
          }}
          options={props.lookups?.rssFeedType}
        />

        <Form.Text name="name" label="Name" isLoading={props.loading} />
        <Form.TextArea
          name="description"
          rows={3}
          label="Description"
          isLoading={props.loading}
        />
      </Form.PanelSection>

      <Form.PanelSection
        iconClassName="bi bi-link-45deg"
        title="2. Source"
        style={{ marginTop: 16 }}
      >
        <Form.Url name="url" label="Url" isLoading={props.loading} />
        <Form.Url
          name="baseUrlLink"
          label="BaseUrl Link"
          isLoading={props.loading}
        />
      </Form.PanelSection>

      <Form.PanelSection
        iconClassName="bi bi-sliders"
        title="3. Display Settings"
        style={{ marginTop: 16 }}
      >
        <Form.Number
          min={1}
          name="maxDisplay"
          label="Max Display"
          isLoading={props.loading}
        />
        <Form.Number
          min={1}
          name="highlightDisplayCount"
          label="Highlight Display Count"
          isLoading={props.loading}
        />
      </Form.PanelSection>
    </Drawer.Form>
  );
};
